import { useCallback, useEffect } from 'react';
import { TOOL_SHORTCUTS, type EditorTool } from '../models/editor-tool';
import type { EditorDocument } from './use-editor-document';
import type { Selection } from './use-selection';

interface Props {
  readonly document: EditorDocument;
  readonly selection: Selection;
  readonly onToolChange: (tool: EditorTool) => void;
  readonly onSave: () => void;
  readonly onShowShortcuts: () => void;
  /** Finishes the shape being drawn, if there is one. */
  readonly onCompleteDrawing?: () => void;
  /** Drops the shape being drawn. Reports whether there was one, so Esc can fall through. */
  readonly onCancelDrawing?: () => boolean;
  /** Shows or hides the background ruling. */
  readonly onToggleGrid?: () => void;
  readonly onDuplicate?: () => void;
  /** Opens the command palette. */
  readonly onShowCommands?: () => void;
  /** Opens the find panel and puts the cursor in it. */
  readonly onFind?: () => void;
}

const isTypingTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  return element !== null && ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
};

/**
 * The editor's keyboard shortcuts.
 *
 * Deliberately does nothing while a field has focus: an admin typing a label called "Desk"
 * should not have the D key switch tools underneath them.
 */
export const useEditorShortcuts = ({
  document: editorDocument,
  selection,
  onToolChange,
  onSave,
  onShowShortcuts,
  onCompleteDrawing,
  onCancelDrawing,
  onToggleGrid,
  onDuplicate,
  onShowCommands,
  onFind,
}: Props): void => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) {
        return;
      }

      const modifier = event.metaKey || event.ctrlKey;

      if (modifier && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) {
          editorDocument.redo();
        } else {
          editorDocument.undo();
        }
        return;
      }

      if (modifier && event.key.toLowerCase() === 's') {
        // Ctrl+S means save here, not "save this web page".
        event.preventDefault();
        onSave();
        return;
      }

      // Ctrl+D duplicates in place. Checked with the other modifier combinations, before the
      // bare-letter tool table, where D is the desk tool.
      if (modifier && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        onDuplicate?.();
        return;
      }

      // The conventional palette key, and the reason the editor can afford to have more
      // actions than controls.
      if (modifier && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onShowCommands?.();
        return;
      }

      // Ctrl+F means find *in this plan*, not in the rendered page — the canvas holds none of
      // the text being searched for, so the browser's own find would report nothing at all.
      if (modifier && event.key.toLowerCase() === 'f') {
        event.preventDefault();
        onFind?.();
        return;
      }

      if (modifier && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        selection.selectAll();
        return;
      }

      // The conventional key for "what can I press here", and the one the `?` button in
      // the top bar mirrors.
      if (event.key === '?') {
        event.preventDefault();
        onShowShortcuts();
        return;
      }

      if (event.key === 'Escape') {
        // Esc is a cascade, not one action. A half-drawn polygon is the nearest thing to
        // cancel; dropping the tool and the selection as well would throw away three things
        // when the admin asked to abandon one.
        if (onCancelDrawing?.()) {
          return;
        }

        onToolChange('select');
        selection.clear();
        return;
      }

      // Closes the shape being drawn, which is the other half of the polygon gesture: click
      // the first vertex again, or press Enter.
      if (event.key === 'Enter' && onCompleteDrawing) {
        event.preventDefault();
        onCompleteDrawing();
        return;
      }

      // Tool shortcuts stay clear of the modifier combinations above.
      if (modifier) {
        return;
      }

      // Checked before the tool table, which is keyed on the bare letter and would otherwise
      // win.
      if (event.key.toLowerCase() === 'g') {
        event.preventDefault();
        onToggleGrid?.();
        return;
      }

      const tool = TOOL_SHORTCUTS[event.key.toLowerCase()];
      if (tool) {
        event.preventDefault();
        onToolChange(tool);
      }
    },
    [
      editorDocument,
      selection,
      onToolChange,
      onSave,
      onShowShortcuts,
      onCompleteDrawing,
      onCancelDrawing,
      onToggleGrid,
      onDuplicate,
      onShowCommands,
      onFind,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export default useEditorShortcuts;
